import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BadgeIcon from "@mui/icons-material/Badge";
import LogoutIcon from "@mui/icons-material/Logout";
import { ROUTES } from "../routes/constants";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { getConfigSync, resolveOidcClientId } from "../config";
import { useRealm } from "../context/RealmContext";

function UserAccountMenu() {
  const { realmId } = useRealm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();

    const config = getConfigSync();
    const resolved = resolveOidcClientId(config, realmId);
    if (!resolved.ok) {
      console.error(
        "[Logout] Cannot resolve OIDC client_id for current realm",
        resolved.reason
      );
      return;
    }

    // userStore must match oidc-client-ts' default (sessionStorage) so this
    // UserManager reads the same User written by react-oidc-context's
    // AuthProvider, ensuring id_token_hint is sent to the IdP's end_session
    // endpoint and the SSO session is actually terminated.
    const userManager = new UserManager({
      authority: config.oidc.authority.trim(),
      client_id: resolved.clientId,
      redirect_uri: window.location.origin,
      post_logout_redirect_uri: window.location.origin,
      userStore: new WebStorageStateStore({ store: sessionStorage }),
    });

    try {
      await userManager.signoutRedirect();
    } catch (err) {
      console.error("[Logout] signoutRedirect failed:", err);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleUserMenuOpen}
        sx={{ color: "rgba(255, 255, 255, 0.9)" }}
        aria-label="User menu"
        aria-controls="user-menu"
        aria-haspopup="true"
      >
        <AccountCircleIcon />
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            Signed In
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1 }} />
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  const isIdentityRoute = location.pathname.startsWith("/identity");
  const isHomePage = location.pathname === "/";

  const handleLogoClick = () => {
    navigate(ROUTES.HOME);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "secondary.dark",
          boxShadow: "none",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important", px: 3 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: "primary.main",
              fontWeight: 700,
              fontSize: "1.5rem",
              cursor: "pointer",
              mr: 2,
            }}
            onClick={handleLogoClick}
          >
            SOVRA
          </Typography>

          <Typography
            variant="subtitle1"
            component="div"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              fontWeight: 500,
              mr: 4,
            }}
          >
            Identity
          </Typography>

          {isIdentityRoute && (
            <>
              <Chip
                icon={<BadgeIcon sx={{ color: "white !important" }} />}
                label="My Identity"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  color: "white",
                  fontWeight: 500,
                  "& .MuiChip-icon": {
                    color: "white",
                  },
                }}
              />
              <Box sx={{ flexGrow: 1 }} />
            </>
          )}

          {isIdentityRoute && <UserAccountMenu />}

          {!isIdentityRoute && <Box sx={{ flexGrow: 1 }} />}
        </Toolbar>
      </AppBar>

      {isIdentityRoute ? (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.default",
          }}
        >
          <Container maxWidth="lg">
            <Outlet />
          </Container>
        </Box>
      ) : isHomePage ? (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Outlet />
        </Box>
      ) : (
        <Container
          maxWidth="lg"
          component="main"
          sx={{
            flexGrow: 1,
            py: 4,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Outlet />
        </Container>
      )}

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderColor: "grey.100",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            {isIdentityRoute
              ? "Sovra Identity • Manage Your Account"
              : `© ${new Date().getFullYear()} Sovra. All rights reserved.`}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default Layout;
